import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';
import { getPublicProductBySlug } from '../lib/productsApi';
import { Currency } from '../data/productModels';
import { isPurchasable, getUnitPriceCents } from '../lib/pricing';
import { useCurrency } from './CurrencyContext';

// Define CartItem locally or re-export if needed.
// We ensure it has the strict shape required for checkout.
export interface CartItem {
  slug: string;
  qty: number;
  currency: Currency;
  unitPriceCents: number;
}

interface CartContextType {
  items: CartItem[];
  cartCurrency: Currency;
  totalQty: number;
  addToCart: (slug: string, qty?: number) => Promise<void>;
  removeFromCart: (slug: string) => void;
  updateQty: (slug: string, qty: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'natuvia_cart_v1';
const MAX_QTY_PER_ITEM = 20;

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currency: globalCurrency } = useCurrency();

  // 1. Initialize State from LocalStorage
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('[Cart] Failed to parse local cart', e);
      return [];
    }
  });

  // 2. Persist to LocalStorage whenever items change
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Ignore storage errors (e.g. private browsing on iOS Safari)
    }
  }, [items]);

  // Derived State
  const totalQty = useMemo(() => items.reduce((acc, item) => acc + item.qty, 0), [items]);

  // Determine Cart Currency (Snapshot from first item or default to global)
  const cartCurrency = items.length > 0 ? items[0].currency : globalCurrency;

  // --- Actions ---

  const addToCart = async (slug: string, qty: number = 1) => {
    const product = await getPublicProductBySlug(slug);
    if (!product) {
      console.error('[Cart] Product not found:', slug);
      alert('Product not found. Please try again.');
      return;
    }

    // Strict Currency Check:
    // If cart has items in a different currency, we must clear it or block.
    // For a smooth UX, if the user adds an item in a new currency, we assume a switch and Reset.
    let currentItems = [...items];
    if (currentItems.length > 0 && currentItems[0].currency !== globalCurrency) {
      if (
        confirm(
          `Your cart contains items in ${currentItems[0].currency}. Switch to ${globalCurrency} and clear cart?`
        )
      ) {
        currentItems = [];
      } else {
        return; // User cancelled
      }
    }

    // Price Validation
    if (!isPurchasable(product, globalCurrency)) {
      alert('This item is not available in the selected currency.');
      return;
    }

    const unitPriceCents = getUnitPriceCents(product, globalCurrency);
    if (unitPriceCents === null) return;

    // Update Logic
    const existingIdx = currentItems.findIndex(i => i.slug === slug);

    if (existingIdx > -1) {
      // Update existing
      const newQty = Math.min(currentItems[existingIdx].qty + qty, MAX_QTY_PER_ITEM);
      currentItems[existingIdx] = {
        ...currentItems[existingIdx],
        qty: newQty,
        unitPriceCents: unitPriceCents, // Update price to latest
      };
    } else {
      // Add new
      currentItems.push({
        slug,
        qty: Math.min(qty, MAX_QTY_PER_ITEM),
        currency: globalCurrency,
        unitPriceCents: unitPriceCents,
      });
    }

    setItems(currentItems);
  };

  const removeFromCart = (slug: string) => {
    setItems(prev => prev.filter(i => i.slug !== slug));
  };

  const updateQty = (slug: string, qty: number) => {
    if (qty < 1) {
      removeFromCart(slug);
      return;
    }
    const safeQty = Math.min(qty, MAX_QTY_PER_ITEM);
    setItems(prev => prev.map(i => (i.slug === slug ? { ...i, qty: safeQty } : i)));
  };

  const clearCart = () => {
    setItems([]);
  };

  return (
    <CartContext.Provider
      value={{
        items,
        cartCurrency,
        totalQty,
        addToCart,
        removeFromCart,
        updateQty,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};
